import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  FileText,
  Upload,
  Download,
  Eye,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Image as ImageIcon,
  File,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { Badge } from '@components/common/Badge';
import { Button } from '@components/common/Button';
import { PageHeader } from '@components/common/PageHeader';
import { mockAttachments } from '@mocks/attachments';
import { getCurrentApplicant } from '@mocks/applicants';
import { getCurrentApplication } from '@mocks/licenses';
import { getLicenseRequirements } from '@utils/licenseRequirements';

type DocumentStatus = 'UPLOADED' | 'VERIFIED' | 'REJECTED' | 'REQUIRED';

interface Document {
  id: number;
  title: string;
  file_name: string;
  file_type: string;
  size: string;
  uploadedAt: string;
  status: DocumentStatus;
  notes?: string;
}


export const DocumentManager: React.FC = () => {
  const applicant = getCurrentApplicant();
  const activeApplication = getCurrentApplication();
  
  // Get dynamic required documents based on active application
  const requiredDocuments = useMemo(() => {
    if (!activeApplication) {
      // Default documents if no active application
      return [
        { id: 'passport', label: 'Passport Photograph', helpText: 'Recent passport-size photo with white background', required: true },
        { id: 'nin', label: 'NIN Slip', helpText: 'National Identity Number verification slip', required: true },
        { id: 'birth_cert', label: 'Birth Certificate', helpText: 'Valid birth certificate or age declaration', required: true },
        { id: 'medical', label: 'Medical Certificate', helpText: 'Medical fitness certificate from approved center', required: true },
      ];
    }
    
    const requirements = getLicenseRequirements(
      activeApplication.license_category,
      activeApplication.application_type
    );
    
    return requirements?.requiredDocuments || [];
  }, [activeApplication]);
  
  const [documents, setDocuments] = useState<Document[]>(
    mockAttachments.map((att) => ({
      id: att.id,
      title: att.document_type,
      file_name: att.file_name,
      file_type: att.file_type,
      size: `${(att.file_size / 1024).toFixed(0)} KB`,
      uploadedAt: att.uploaded_at,
      status: att.status as DocumentStatus,
      notes: att.notes,
    }))
  );
  const [isUploading, setIsUploading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, docType: string, docId: string) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    // Validate file type - restrict existing license to PDF only
    const allowedTypes = docId === 'existing_license' 
      ? ['application/pdf']
      : ['image/jpeg', 'image/png', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    
    if (!allowedTypes.includes(file.type)) {
      if (docId === 'existing_license') {
        toast.error('Existing license copy must be in PDF format only');
      } else {
        toast.error('Only JPEG, PNG, PDF, DOC, and DOCX files are allowed');
      }
      return;
    }

    setIsUploading(true);

    // Mock upload delay
    await new Promise((resolve) => setTimeout(resolve, 1500));

    const newDoc: Document = {
      id: Date.now(),
      title: docType,
      file_name: file.name,
      file_type: file.type,
      size: `${(file.size / 1024).toFixed(0)} KB`,
      uploadedAt: new Date().toISOString(),
      status: 'UPLOADED',
    };

    setDocuments([...documents, newDoc]);
    console.log('✅ Mock: Document uploaded', newDoc);
    toast.success(`${docType} uploaded successfully`);
    setIsUploading(false);
  };

  const handleDownload = (doc: Document) => {
    console.log('✅ Mock: Download document', doc.file_name);
    toast.success(`Downloading ${doc.file_name}`);
  };

  const handlePreview = (doc: Document) => {
    setSelectedDoc(doc);
    console.log('✅ Mock: Preview document', doc.file_name);
  };

  const handleDelete = async (docId: number) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    console.log('✅ Mock: Delete document', docId);
    setDocuments(documents.filter((d) => d.id !== docId));
    toast.success('Document deleted successfully');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusConfig = (status: DocumentStatus) => {
    switch (status) {
      case 'VERIFIED':
        return {
          variant: 'success' as const,
          icon: <CheckCircle className="h-4 w-4" />,
          label: 'Verified',
        };
      case 'REJECTED':
        return {
          variant: 'danger' as const,
          icon: <XCircle className="h-4 w-4" />,
          label: 'Rejected',
        };
      case 'UPLOADED':
        return {
          variant: 'warning' as const,
          icon: <Clock className="h-4 w-4" />,
          label: 'Under Review',
        };
      default:
        return {
          variant: 'default' as const,
          icon: <AlertCircle className="h-4 w-4" />,
          label: 'Required',
        };
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) {
      return <ImageIcon className="h-5 w-5 text-blue-500" />;
    } else if (fileType === 'application/pdf') {
      return <FileText className="h-5 w-5 text-red-500" />;
    }
    return <File className="h-5 w-5 text-gray-500" />;
  };

  const getDocumentStats = () => {
    const total = requiredDocuments.length;
    const uploaded = documents.filter((d) => d.status !== 'REQUIRED').length;
    const verified = documents.filter((d) => d.status === 'VERIFIED').length;
    return { total, uploaded, verified };
  };

  const stats = getDocumentStats();

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <PageHeader
        title="Document Manager"
        description={activeApplication 
          ? `Upload and manage required documents for your ${activeApplication.application_type === 'NEW' ? 'New' : 'Renewal'} ${activeApplication.license_category} license application`
          : "Upload and manage your required documents for license application"
        }
      />

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.uploaded}/{stats.total}</p>
              <p className="text-sm text-gray-600">Documents Uploaded</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.verified}</p>
              <p className="text-sm text-gray-600">Verified</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-orange-100 rounded-lg">
              <AlertCircle className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total - stats.uploaded}</p>
              <p className="text-sm text-gray-600">Pending Upload</p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Required Documents */}
        <Card title="Required Documents" icon={<FileText className="h-5 w-5" />}>
          <div className="space-y-4">
            {requiredDocuments.map((reqDoc) => {
              const uploadedDoc = documents.find((d) => d.title === reqDoc.label);
              const status = uploadedDoc ? uploadedDoc.status : 'REQUIRED';
              const statusConfig = getStatusConfig(status);
              const acceptedFormats = reqDoc.id === 'existing_license' 
                ? 'application/pdf' 
                : 'image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

              return (
                <div
                  key={reqDoc.id}
                  className="p-4 border border-gray-200 rounded-lg hover:border-ncaa-light transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">
                        {reqDoc.label}
                        {reqDoc.required && <span className="text-red-500 ml-1">*</span>}
                      </h4>
                      {reqDoc.helpText && (
                        <p className="text-sm text-gray-600 mt-1">{reqDoc.helpText}</p>
                      )}
                      {reqDoc.id === 'existing_license' && (
                        <p className="text-xs text-orange-600 mt-1 font-medium">PDF format only</p>
                      )}
                    </div>
                    <Badge variant={statusConfig.variant} icon={statusConfig.icon}>
                      {statusConfig.label}
                    </Badge>
                  </div>

                  {uploadedDoc && uploadedDoc.notes && (
                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                      <strong>Note:</strong> {uploadedDoc.notes}
                    </div>
                  )}

                  <div className="mt-3">
                    <label className="block">
                      <input
                        type="file"
                        accept={acceptedFormats}
                        onChange={(e) => handleFileUpload(e, reqDoc.label, reqDoc.id)}
                        disabled={isUploading}
                        className="hidden"
                        id={`upload-${reqDoc.id}`}
                      />
                      <Button
                        as="label"
                        htmlFor={`upload-${reqDoc.id}`}
                        size="sm"
                        variant={uploadedDoc ? 'outline' : 'primary'}
                        icon={<Upload className="h-4 w-4" />}
                        loading={isUploading}
                        className="cursor-pointer"
                      >
                        {uploadedDoc ? 'Replace' : 'Upload'}
                      </Button>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h5 className="text-sm font-semibold text-blue-900 mb-2">Upload Guidelines</h5>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• Accepted formats: JPEG, PNG, PDF, DOC, DOCX</li>
                <li>• Existing license copy must be in PDF format only</li>
                <li>• Maximum file size: 10MB per document</li>
                <li>• Ensure documents are clear and legible</li>
                <li>• All required documents (marked with *) must be uploaded before submission</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Uploaded Documents */}
        <Card title="My Documents" icon={<FileText className="h-5 w-5" />}>
          {documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-sm">No documents uploaded yet</p>
              <p className="text-gray-400 text-xs mt-1">Start uploading your required documents</p>
            </div>
          ) : (
            <div className="space-y-3">
              {documents.map((doc) => {
                const statusConfig = getStatusConfig(doc.status);

                return (
                  <div
                    key={doc.id}
                    className="p-4 border border-gray-200 rounded-lg hover:border-ncaa-light transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">{getFileIcon(doc.file_type)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-1">
                          <div>
                            <h4 className="font-semibold text-gray-900 text-sm">{doc.title}</h4>
                            <p className="text-xs text-gray-500 mt-0.5">{doc.file_name}</p>
                          </div>
                          <Badge variant={statusConfig.variant} icon={statusConfig.icon}>
                            {statusConfig.label}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
                          <span>{doc.size}</span>
                          <span>•</span>
                          <span>Uploaded {formatDate(doc.uploadedAt)}</span>
                        </div>
                        {doc.notes && (
                          <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                            {doc.notes}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                      <Button
                        size="sm"
                        variant="outline"
                        icon={<Eye className="h-3 w-3" />}
                        onClick={() => handlePreview(doc)}
                      >
                        Preview
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        icon={<Download className="h-3 w-3" />}
                        onClick={() => handleDownload(doc)}
                      >
                        Download
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        icon={<Trash2 className="h-3 w-3" />}
                        onClick={() => handleDelete(doc.id)}
                        className="ml-auto text-red-600 hover:text-red-700 hover:border-red-300"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Preview Modal */}
      {selectedDoc && (
        <div
          className="fixed inset-0 bg-gray-900 bg-opacity-40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedDoc(null)}
        >
          <div
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{selectedDoc.title}</h3>
                <p className="text-sm text-gray-600">{selectedDoc.file_name}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setSelectedDoc(null)}>
                Close
              </Button>
            </div>
            <div className="p-6 bg-gray-50 overflow-auto max-h-[calc(90vh-100px)]">
              {selectedDoc.file_type.startsWith('image/') ? (
                <div className="flex items-center justify-center">
                  <div className="bg-white p-4 rounded-lg shadow-lg">
                    <div className="w-full h-96 bg-gray-200 flex items-center justify-center">
                      <ImageIcon className="h-16 w-16 text-gray-400" />
                      <p className="text-gray-500 ml-4">Image preview (mock)</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                  <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">PDF preview not available in mock mode</p>
                  <Button className="mt-4" onClick={() => handleDownload(selectedDoc)}>
                    Download Document
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
